import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Viewer } from '../viewer/viewer';
import type { Subscription } from '@pointorama/potree-shared';
import {
  PropertyType,
  getGlobalPropertyService,
  getAnnotationVisibleProperty,
  getSketchObjectColorProperty,
  getSketchObjectDimensionDecimalsProperty,
  getSketchObjectDimensionShowUnitProperty,
  getSketchObjectDimensionTextAlongLineProperty,
  getSketchObjectDimensionTextSizeProperty,
  getSketchObjectTypeIdProperty,
  getSketchObjectAnchorProperty,
  getSketchObjectRotationProperty,
  getSketchObjectWidthProperty,
  getSketchObjectDepthProperty,
  getSketchObjectWallEdgeIdProperty,
  getSketchObjectWallOffsetProperty,
  getSketchObjectWallSideProperty,
  getSketchObjectWallInsetProperty,
  getWallSketchNodesProperty,
  getWallSketchEdgesProperty,
  getFloorWallSourceProperty,
} from '@pointorama/potree-shared';
import { getSketchObjectType, onSketchObjectTypesChanged, type RegisteredSketchObjectType } from '../sketchObjects/registry';
import { SketchObjectAnchorKind, type SketchObjectFrame } from '../sketchObjects/types';
import { cloneCustomModel } from '../sketchObjects/custom/modelCache';
import { modelWorldMatrix } from '../sketchObjects/custom/modelPlacement';
import { MODEL_HEIGHT_PARAM, placedDimensionOffset } from '../sketchObjects/custom/customSketchObjectType';
import { at, buildFrameFromAnchor, buildFrameFromWall, buildFrameFromWallFaceLine, frameCorners, pushDashedRect, pushDashedSegment, SKETCH_OBJECT_Z_LIFT } from '../sketchObjects/glyphs/frame';
import { pointAtArcLength } from './skeleton/arcGeometry';
import { clipSegmentsOutsideWalls } from '../sketchObjects/glyphs/wallClip';
import { SKELETON_SELECTION_COLOR } from '../personas/tools/modules/selection/WallSketchSelection';
import { applyBackdropInvert } from './BackdropContrast';
import { PixelHitTester } from '../personas/tools/modules/selection/PixelHitTester';
import { isAnnotationPickBlocked } from '../viewer/AnnotationBlacklistStack';
import type { MeasuredWallFaceHit, PointoramaWallSketch } from './PointoramaWallSketch';
import { DimensionLabel } from './DimensionLabel';
import { followWalls, type WallGeometry } from '../sketchObjects/dimensionWallFollow';

/** Line width of the glyph symbol, in pixels (matches the wall outline). */
const GLYPH_LINE_WIDTH = 3;

/** Glyphs draw just above the wall skin fill (1) and outline (2). */
const GLYPH_RENDER_ORDER = 3;
const GLYPH_RENDER_ORDER_SELECTED = 4;

const GLYPH_Z_LIFT = SKETCH_OBJECT_Z_LIFT;

/**
 * Scene object for a SKETCH_OBJECT (furniture/fixture symbol in the 2D floor
 * layout: bath, chair, cabinet, …).
 *
 * The object's type comes from the sketch-object registry
 * (`sketchobject_type_id`); all placement + params live in reactive
 * properties. The glyph (SVG- or parametrically-built line segments) renders
 * as this object's own LineSegments2 child, so per-object `.visible` flips
 * (annotation blacklist, per-floor filtering) work like every other
 * annotation. Vertices are stored relative to the frame center with the
 * object positioned at the center — keeps float32 data small at UTM scale.
 *
 * Wall-attached objects (constraint WALL) re-derive their frame from the live
 * wall edge on every rebuild and re-render when the owning wall sketch's
 * skeleton changes, so they follow wall edits.
 */
export class PointoramaSketchObject extends THREE.Object3D {
  private _identifier = '';
  groupIdentifier: string | null = null;
  selected = false;
  finished = true;

  private viewer: Viewer;
  private color: THREE.Color;

  private lineGeometry: LineSegmentsGeometry;
  private line: LineSegments2;
  public lineMaterial: LineMaterial;

  /** Glyph segments in WORLD coords, flat [x,y,z, x,y,z, ...] — pixel hit-testing. */
  private _worldSegments: number[] = [];

  /** Whether the line material currently carries the backdrop-invert patch. */
  private _backdropInverted = false;

  private _colorSubscription?: Subscription;
  private _visibleSubscription?: Subscription;
  private _geometrySubscriptions: Subscription[] = [];
  /** Wall-sketch skeleton subscriptions (wall-attached objects), keyed by sketch id. */
  private _wallSubscriptions = new Map<string, Subscription[]>();
  /** The 3D model of an organisation object (3D views only), and the asset it shows. */
  private modelGroup: THREE.Group | null = null;
  private modelAssetId = '';
  /** The measured-length label of a dimension annotation (dimension types only). */
  private dimensionLabel: DimensionLabel | null = null;
  /**
   * A dimension follows the walls of its floor (see dimensionWallFollow):
   * the sketch it watches, that sketch's data as last seen (the "before" of
   * the next change) and the subscriptions.
   */
  private wallFollow: { sketchId: string; before: WallGeometry; subscriptions: Subscription[] } | null = null;
  /** Labels follow the user's meter / feet preference. */
  private readonly onLengthUnitChanged = (): void => {
    if (this.dimensionLabel) this.rebuildGlyph();
  };

  /**
   * Organisation (custom) types register after the project's objects load and
   * are re-registered when their definition is republished: reload then, so
   * the param subscriptions and the glyph follow the current definition.
   * Built-in types never change at runtime.
   */
  private unsubscribeTypes = onSketchObjectTypesChanged(() => {
    const typeId = this._identifier ? getSketchObjectTypeIdProperty(this._identifier).value : '';
    if (typeId && (!getSketchObjectType(typeId) || getSketchObjectType(typeId)?.custom)) this.loadReactiveProperties();
  });

  get identifier(): string {
    return this._identifier;
  }

  set identifier(value: string) {
    this._identifier = value;
    if (value) this.loadReactiveProperties();
  }

  constructor(viewer: Viewer, args: { color?: THREE.Color } = {}) {
    super();
    this.viewer = viewer;
    // Pre-property fallback matching `sketchobject_color`'s black default, so
    // the glyph never flashes another color before the identifier is set.
    this.color = args.color ?? new THREE.Color(0x000000);

    const rendererSize = new THREE.Vector2();
    this.viewer.renderer.getSize(rendererSize);

    this.lineGeometry = new LineSegmentsGeometry();
    // Depth-ignoring like the opening sketches / skeleton lines: the glyph
    // must stay visible over the point cloud in the 3D view (stacking within
    // the floorplan scene comes from renderOrder, not depth).
    this.lineMaterial = new LineMaterial({
      color: this.color,
      linewidth: GLYPH_LINE_WIDTH,
      resolution: rendererSize,
      depthTest: false,
      depthWrite: false,
    });
    this.line = new LineSegments2(this.lineGeometry, this.lineMaterial);
    this.line.renderOrder = GLYPH_RENDER_ORDER;
    // Vertices are re-localized around the frame center on every rebuild;
    // bounds would be stale — the glyph sits on the visible floor anyway.
    this.line.frustumCulled = false;
    this.add(this.line);
    // Install the backdrop-invert patch for the (default) black color.
    this.applyMaterialColor();
    this.viewer.addEventListener('length_unit_changed', this.onLengthUnitChanged);
  }

  private loadReactiveProperties(): void {
    if (!this._identifier) return;
    try {
      this.unsubscribeAll();

      // Dedicated color property (NOT the generic `annotation_color`) — its
      // default is black rather than the user default object color.
      const colorProp = getSketchObjectColorProperty(this._identifier);
      const visibleProp = getAnnotationVisibleProperty(this._identifier);

      if (colorProp.value) this.color = new THREE.Color(colorProp.value);
      this.visible = visibleProp.value;
      this.applyMaterialColor();

      this._colorSubscription = colorProp.subscribeToChanges((value: string) => {
        if (value) {
          this.color = new THREE.Color(value);
          this.applyMaterialColor();
        }
      });
      this._visibleSubscription = visibleProp.subscribeToChanges((value) => {
        this.visible = value;
      });

      const type = getSketchObjectType(getSketchObjectTypeIdProperty(this._identifier).value);
      const onGeometryChange = () => this.rebuildGlyph();
      // Width/depth/param changes first give the type a chance to keep its
      // coupled params consistent (e.g. stair step ↔ number of steps) — the
      // sync's own writes re-enter through these subscriptions and settle
      // because equal writes don't notify.
      const onSyncedChange = (changedKey: string) => () => {
        type?.syncParams?.(this._identifier, changedKey);
        this.rebuildGlyph();
      };

      // Type change is rare (never through the UI today) but would swap the
      // param subscription set — reload everything.
      this._geometrySubscriptions.push(
        getSketchObjectTypeIdProperty(this._identifier).subscribeToChanges(() => this.loadReactiveProperties()),
      );

      this._geometrySubscriptions.push(getSketchObjectWidthProperty(this._identifier).subscribeToChanges(onSyncedChange('width')));
      this._geometrySubscriptions.push(getSketchObjectDepthProperty(this._identifier).subscribeToChanges(onSyncedChange('depth')));
      const geometryProps = [
        getSketchObjectAnchorProperty(this._identifier),
        getSketchObjectRotationProperty(this._identifier),
        getSketchObjectWallEdgeIdProperty(this._identifier),
        getSketchObjectWallOffsetProperty(this._identifier),
        getSketchObjectWallSideProperty(this._identifier),
        getSketchObjectWallInsetProperty(this._identifier),
      ];
      for (const prop of geometryProps) {
        this._geometrySubscriptions.push(prop.subscribeToChanges(onGeometryChange));
      }

      // Per-type params (door count, swings, …) redraw the glyph too.
      if (type) {
        const service = getGlobalPropertyService();
        const paramKeyPrefix = `sketchobject_${type.id}_`;
        for (const param of type.initialParamProperties()) {
          const prop = service.getProperty(param.identifier, this._identifier, PropertyType.PROJECT_GLOBAL);
          this._geometrySubscriptions.push(prop.subscribeToChanges(onSyncedChange(param.identifier.slice(paramKeyPrefix.length))));
        }
        // Normalize coupled params once on load (e.g. a drawn stair's step
        // count follows its actual run length, not the schema default).
        type.syncParams?.(this._identifier, 'width');
        // A dimension's label shows its length with the chosen decimals.
        if (type.dimension) {
          this._geometrySubscriptions.push(
            getSketchObjectDimensionDecimalsProperty(this._identifier).subscribeToChanges(onGeometryChange),
            getSketchObjectDimensionShowUnitProperty(this._identifier).subscribeToChanges(onGeometryChange),
            getSketchObjectDimensionTextAlongLineProperty(this._identifier).subscribeToChanges(onGeometryChange),
            getSketchObjectDimensionTextSizeProperty(this._identifier).subscribeToChanges(onGeometryChange),
          );
        }
      }

      this.rebuildGlyph();
    } catch {
      // property service not ready yet
    }
  }

  /**
   * Default-black glyphs render like the wall outline / door glyphs: per-pixel
   * backdrop inversion (black over the light Plan background, white over the
   * dark one). An explicit user-chosen color — and the selection color — stays
   * solid. The invert patch lives in the shader, so flipping it means swapping
   * in a fresh material.
   */
  private applyMaterialColor(): void {
    const invert = !this.selected && this.color.getHex() === 0x000000;
    if (invert !== this._backdropInverted) {
      this._backdropInverted = invert;
      const rendererSize = new THREE.Vector2();
      this.viewer.renderer.getSize(rendererSize);
      this.lineMaterial.dispose();
      this.lineMaterial = new LineMaterial({
        color: this.color,
        linewidth: GLYPH_LINE_WIDTH,
        resolution: rendererSize,
        depthTest: false,
        depthWrite: false,
      });
      if (invert) applyBackdropInvert(this.lineMaterial, this.viewer.backdropCapture);
      this.line.material = this.lineMaterial;
    }
    this.lineMaterial.color = this.selected ? SKELETON_SELECTION_COLOR : this.color;
  }

  /** The registry definition of this object's type, or null if unknown/not loaded. */
  getType(): RegisteredSketchObjectType | null {
    return getSketchObjectType(getSketchObjectTypeIdProperty(this._identifier).value);
  }

  /**
   * Find the wall sketch owning `edgeId` plus the edge's geometry.
   *
   * Pipeline edge ids are only unique PER SKETCH (per-run counters like
   * `ss_e_0`), so on a multi-floor project every floor's sketch reuses the
   * same ids. The object's OWN floor's sketch is searched first — a colliding
   * id on another floor would otherwise win by scene order and teleport the
   * object onto that floor's wall. Other sketches remain a fallback for
   * legacy objects without a group.
   */
  private resolveWallEdge(edgeId: string): { wallSketch: PointoramaWallSketch; start: { x: number; y: number }; end: { x: number; y: number }; leftSize: number; rightSize: number; bulge: number } | null {
    if (!edgeId) return null;
    const sketches = this.viewer.scene.wallSketches;
    const ordered = this.groupIdentifier ? [...sketches.filter((s) => s.groupIdentifier === this.groupIdentifier), ...sketches.filter((s) => s.groupIdentifier !== this.groupIdentifier)] : sketches;
    for (const ws of ordered) {
      const edge = ws.getEdge(edgeId);
      if (!edge) continue;
      const a = ws.getNode(edge.startNodeId);
      const b = ws.getNode(edge.endNodeId);
      if (!a || !b) continue;
      return {
        wallSketch: ws,
        start: { x: a.position.x, y: a.position.y },
        end: { x: b.position.x, y: b.position.y },
        leftSize: edge.leftSize ?? 0.1,
        rightSize: edge.rightSize ?? 0.1,
        bulge: edge.bulge ?? 0,
      };
    }
    return null;
  }

  /**
   * The object's current world frame — FREE objects from their persisted
   * anchor + rotation, WALL objects re-derived from the live wall edge (so
   * they follow wall edits). Null when unresolvable (unknown type, empty
   * anchor, missing edge).
   */
  resolveFrame(): SketchObjectFrame | null {
    const type = this.getType() ?? this.placeholderShape();
    const width = getSketchObjectWidthProperty(this._identifier).value ?? type.defaultSize.width;
    const depth = getSketchObjectDepthProperty(this._identifier).value ?? type.defaultSize.depth;

    if (type.constraint.kind === 'WALL') {
      const edgeId = getSketchObjectWallEdgeIdProperty(this._identifier).value || '';
      const resolved = this.resolveWallEdge(edgeId);
      if (!resolved) return null;
      this.subscribeToWallSketch(resolved.wallSketch);
      const side = getSketchObjectWallSideProperty(this._identifier).value;
      const offset = getSketchObjectWallOffsetProperty(this._identifier).value ?? 0;
      const inset = getSketchObjectWallInsetProperty(this._identifier).value ?? 0;
      // MEASURED view: rest the object's back on the MEASURED face LINE
      // instead of the authored sketch face — measured faces need not be
      // parallel to the sketch edge, so both position AND rotation follow
      // the face (display-only; the persisted parametric placement is
      // untouched — the same helper drives the placement ghost, so preview
      // and render always agree). Authored face everywhere else.
      const z = resolved.wallSketch.getFloorHeight() + GLYPH_Z_LIFT;
      const faceLine = resolved.wallSketch.effectiveMeasuredFaceLineAt(edgeId, side, offset + width / 2, width / 2);
      if (faceLine) {
        const anchorOnCenterline = pointAtArcLength(resolved.start, resolved.end, resolved.bulge, offset + width / 2).point;
        return buildFrameFromWallFaceLine(anchorOnCenterline, faceLine, side, inset, width, depth, z);
      }
      return buildFrameFromWall(resolved.start, resolved.end, offset, side, side ? resolved.leftSize : resolved.rightSize, inset, width, depth, z, resolved.bulge);
    }

    const anchor = getSketchObjectAnchorProperty(this._identifier).value;
    if (anchor.length === 0) return null;
    const rotation = getSketchObjectRotationProperty(this._identifier).value ?? 0;
    const z = anchor[0].z + GLYPH_Z_LIFT;
    const frame = buildFrameFromAnchor(type.anchor, anchor, rotation, width, depth, z);
    // MEASURED view: the band is usually thicker than the sketch band a free
    // object was placed flush against — re-run the anti-clip push against the
    // measured faces, display-only (the persisted anchor never changes).
    // Dimensions stay on the points they measure.
    if (!frame || this.getType()?.dimension) return frame;
    return this.displaceFrameOutOfMeasuredBand(frame);
  }

  /**
   * Placement shape of an object whose type is not registered (an
   * organisation object this client has not loaded, or a type from a newer
   * build), inferred from what it stores, so it still draws a placeholder
   * where it was placed instead of vanishing.
   */
  private placeholderShape(): Pick<RegisteredSketchObjectType, 'constraint' | 'anchor' | 'defaultSize'> {
    const onWall = !!getSketchObjectWallEdgeIdProperty(this._identifier).value;
    const anchorPoints = getSketchObjectAnchorProperty(this._identifier).value.length;
    return {
      constraint: onWall ? { kind: 'WALL' } : { kind: 'FREE' },
      anchor: anchorPoints >= 2 ? SketchObjectAnchorKind.LINE : SketchObjectAnchorKind.POINT,
      defaultSize: { width: 1, depth: 1 },
    };
  }

  /** Whether the MEASURED representation is active for this object's floor. */
  private isMeasuredRepresentation(sketch: PointoramaWallSketch): boolean {
    return this.viewer.floorplanRepresentation?.isOriginalActive(sketch.groupIdentifier) ?? false;
  }

  /**
   * Display-time anti-clip for FREE objects in the MEASURED view: when the
   * object's footprint pokes past the measured wall FACE LINE (which is
   * thicker, thinner or slightly rotated relative to the sketch band it was
   * placed against), the contact resolves like a physics push: a small
   * TORQUE first (≤15° — the wall-facing side spins parallel to the face
   * when it's already nearly so), then a push along the face's own normal
   * until the deepest corner clears it — the same flush "physics" the
   * placement ran, re-applied to the measured geometry. The persisted
   * anchor/rotation is never
   * modified; leaving the Measured view restores the placed position.
   * Straight wall edges only, deepest penetration wins, absurd pushes
   * (>1.5 m) are refused.
   */
  private displaceFrameOutOfMeasuredBand(frame: SketchObjectFrame): SketchObjectFrame {
    const floorId = this.groupIdentifier;
    if (!floorId) return frame;
    const sketch = this.viewer.scene.wallSketches.find((s) => s.groupIdentifier === floorId);
    if (!sketch || !this.isMeasuredRepresentation(sketch)) return frame;
    this.subscribeToWallSketch(sketch);

    const corners = frameCorners(frame);
    let best: { push: number; nx: number; ny: number; face: MeasuredWallFaceHit } | null = null;

    for (const edge of sketch.getEdges()) {
      if (Math.abs(edge.bulge ?? 0) > 1e-6) continue;
      const a = sketch.getNode(edge.startNodeId);
      const b = sketch.getNode(edge.endNodeId);
      if (!a || !b) continue;
      const dx = b.position.x - a.position.x;
      const dy = b.position.y - a.position.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-6) continue;
      const ux = dx / len;
      const uy = dy / len;
      const nx = -uy;
      const ny = ux;

      // Object footprint in the edge frame (s along the edge).
      let minS = Infinity;
      let maxS = -Infinity;
      for (const c of corners) {
        const s = (c.x - a.position.x) * ux + (c.y - a.position.y) * uy;
        minS = Math.min(minS, s);
        maxS = Math.max(maxS, s);
      }
      if (maxS < 0 || minS > len) continue; // no along-span overlap

      const cN = (frame.center.x - a.position.x) * nx + (frame.center.y - a.position.y) * ny;
      const spanMid = Math.max(0, Math.min(len, (minS + maxS) / 2));
      const faces = sketch.measuredWallFacesAt({
        center: { x: a.position.x + ux * spanMid, y: a.position.y + uy * spanMid },
        wallDir: { x: ux, y: uy },
        wallNorm: { x: nx, y: ny },
        halfWidth: Math.max((maxS - minS) / 2, 0.05),
      });

      // Collide against the measured face LINE on the object's side — the
      // face may be angled relative to the sketch edge, so both the
      // penetration depth and the push direction come from the face itself
      // (the old scalar-band check misjudged angled faces). No measured face
      // on that side → nothing to collide with (an authored-size fallback
      // manufactured phantom collisions where the measured band has no wall).
      const sideSign = cN >= 0 ? 1 : -1;
      const face = sideSign > 0 ? faces.left : faces.right;
      if (!face) continue;
      const fnx = -face.dir.y * sideSign;
      const fny = face.dir.x * sideSign;
      let minD = Infinity;
      for (const c of corners) {
        minD = Math.min(minD, (c.x - face.point.x) * fnx + (c.y - face.point.y) * fny);
      }
      const penetration = -minD; // deepest corner behind the face line
      if (penetration <= 1e-4) continue;
      // Fully-through objects (deeper than the whole band) are not "clipping
      // a wall" — leave them alone rather than teleporting them across.
      const bandDepth = (faces.left?.distance ?? edge.leftSize ?? 0.1) + (faces.right?.distance ?? edge.rightSize ?? 0.1);
      if (penetration >= bandDepth) continue;
      if (!best || penetration > best.push) {
        best = { push: penetration, nx: fnx, ny: fny, face };
      }
    }

    if (!best || best.push > 1.5) return frame;

    // Physics-style fit: the wall contact also TORQUES the object — when its
    // wall-facing side is already nearly parallel to the measured face, spin
    // it about its center to sit flush (a deliberately angled object stays
    // translate-only). Then push out along the face normal by the deepest
    // remaining corner penetration.
    const MAX_FIT_ROTATION = (15 * Math.PI) / 180;
    let adjusted = frame;
    const facingCandidates = [frame.dir, { x: -frame.dir.x, y: -frame.dir.y }, frame.norm, { x: -frame.norm.x, y: -frame.norm.y }];
    let facing = facingCandidates[0];
    let facingDot = Infinity;
    for (const candidate of facingCandidates) {
      const d = candidate.x * best.nx + candidate.y * best.ny;
      if (d < facingDot) {
        facingDot = d;
        facing = candidate;
      }
    }
    // Rotation that turns the facing side's outward normal onto −faceNormal
    // (side exactly parallel to the face line).
    const targetX = -best.nx;
    const targetY = -best.ny;
    const delta = Math.atan2(facing.x * targetY - facing.y * targetX, facing.x * targetX + facing.y * targetY);
    if (Math.abs(delta) > 1e-4 && Math.abs(delta) <= MAX_FIT_ROTATION) {
      const cos = Math.cos(delta);
      const sin = Math.sin(delta);
      const rotate = (v: { x: number; y: number }): { x: number; y: number } => ({ x: v.x * cos - v.y * sin, y: v.x * sin + v.y * cos });
      adjusted = { ...frame, dir: rotate(frame.dir), norm: rotate(frame.norm) };
    }

    // Re-measure against the face line with the (possibly rotated) footprint.
    let minD = Infinity;
    for (const c of frameCorners(adjusted)) {
      minD = Math.min(minD, (c.x - best.face.point.x) * best.nx + (c.y - best.face.point.y) * best.ny);
    }
    const push = -minD;
    if (push > 1.5) return frame;
    if (push > 1e-4) {
      adjusted = { ...adjusted, center: { x: adjusted.center.x + best.nx * push, y: adjusted.center.y + best.ny * push } };
    }
    return adjusted;
  }

  /** Follow wall edits: redraw when the owning sketch's skeleton changes —
   *  and when the floor's wall SOURCE flips (the measured face substitution /
   *  anti-clip displacement in resolveFrame depends on the representation). */
  private subscribeToWallSketch(wallSketch: PointoramaWallSketch): void {
    if (this._wallSubscriptions.has(wallSketch.identifier)) return;
    const onSkeletonChange = () => this.rebuildGlyph();
    const subscriptions = [getWallSketchNodesProperty(wallSketch.identifier).subscribeToChanges(onSkeletonChange), getWallSketchEdgesProperty(wallSketch.identifier).subscribeToChanges(onSkeletonChange)];
    if (wallSketch.groupIdentifier) {
      try {
        subscriptions.push(getFloorWallSourceProperty(wallSketch.groupIdentifier).subscribeToChanges(onSkeletonChange));
      } catch {
        // property service hiccup — tab-change rebuilds still cover it
      }
    }
    this._wallSubscriptions.set(wallSketch.identifier, subscriptions);
  }

  /** Rebuild the glyph line from the current frame + params. */
  rebuildGlyph(): void {
    const type = this.getType();
    const frame = this.resolveFrame();
    if (!frame) {
      this._worldSegments = [];
      this.setLocalSegments([0, 0, 0, 0, 0, 0]);
      this.removeModel();
      this.removeDimensionLabel();
      return;
    }

    let segments = type ? type.buildGlyph(frame, this._identifier) : placeholderGlyph(frame);
    // Beams (`drawLikeWall`) end ON wall centerlines, so their outline is
    // clipped against the wall bodies — the dashed rectangle visually stops
    // at the wall FACE instead of drawing across the wall fill. Subscribing
    // to the sketch keeps the clip in step with wall edits. Dimensions draw
    // across walls (they measure them).
    if (type?.drawLikeWall && !type.dimension) {
      const wallSketch = this.viewer.scene.wallSketches.find((s) => s.groupIdentifier === this.groupIdentifier);
      if (wallSketch) {
        this.subscribeToWallSketch(wallSketch);
        segments = clipSegmentsOutsideWalls(segments, wallSketch);
      }
    }
    this._worldSegments = segments;
    // Selection-only extras (e.g. the beam's centerline) render as part of the
    // same line object — select()/deselect() rebuild to add/remove them. Kept
    // out of `_worldSegments` so hit-testing tracks the base glyph only.
    const selectionSegments = this.selected && type ? type.buildSelectionGlyph(frame, this._identifier) : [];
    const drawSegments = selectionSegments.length > 0 ? [...this._worldSegments, ...selectionSegments] : this._worldSegments;

    // Localize: object sits at the frame center, vertices relative to it.
    const local = new Array<number>(drawSegments.length);
    for (let i = 0; i < drawSegments.length; i += 3) {
      local[i] = drawSegments[i] - frame.center.x;
      local[i + 1] = drawSegments[i + 1] - frame.center.y;
      local[i + 2] = drawSegments[i + 2];
    }
    this.position.set(frame.center.x, frame.center.y, 0);
    this.setLocalSegments(local.length > 0 ? local : [0, 0, 0, 0, 0, 0]);
    this.updateModel(type, frame);
    this.updateDimensionLabel(type, frame);
    if (type?.dimension) this.ensureWallFollow();
  }

  /** Start watching the floor's wall sketch (it may load after the dimension; rebuilds retry). */
  private ensureWallFollow(): void {
    const sketch = this.viewer.scene.wallSketches.find((s) => s.groupIdentifier === this.groupIdentifier);
    if (!sketch || this.wallFollow?.sketchId === sketch.identifier) return;
    this.stopWallFollow();
    try {
      const nodes = getWallSketchNodesProperty(sketch.identifier);
      const edges = getWallSketchEdgesProperty(sketch.identifier);
      const onWalls = () => this.followWallChange(sketch.identifier);
      this.wallFollow = {
        sketchId: sketch.identifier,
        before: { nodes: nodes.value, edges: edges.value },
        subscriptions: [nodes.subscribeToChanges(onWalls), edges.subscribeToChanges(onWalls)],
      };
    } catch {
      // property service not ready — the next rebuild retries
    }
  }

  /**
   * The walls changed: move the ends that sat on them along (corners stay
   * corners, face points keep their place along the face) and store the new
   * ends — length, label, panel and exports follow the stored anchor.
   */
  private followWallChange(sketchId: string): void {
    const follow = this.wallFollow;
    if (!follow || follow.sketchId !== sketchId) return;
    const after: WallGeometry = { nodes: getWallSketchNodesProperty(sketchId).value, edges: getWallSketchEdgesProperty(sketchId).value };
    const before = follow.before;
    follow.before = after;
    const anchorProperty = getSketchObjectAnchorProperty(this._identifier);
    const anchor = anchorProperty.value;
    if (anchor.length < 2) return;
    const moved = anchor.map((p) => {
      const next = followWalls(p, before, after);
      return next ? { x: next.x, y: next.y, z: p.z } : p;
    });
    if (!moved.some((p, i) => Math.hypot(p.x - anchor[i].x, p.y - anchor[i].y) > 1e-6)) return;
    const [a, b] = moved;
    anchorProperty.value = moved;
    getSketchObjectRotationProperty(this._identifier).value = Math.atan2(b.y - a.y, b.x - a.x);
    getSketchObjectWidthProperty(this._identifier).value = Math.hypot(b.x - a.x, b.y - a.y);
  }

  private stopWallFollow(): void {
    this.wallFollow?.subscriptions.forEach((s) => s.unsubscribe());
    this.wallFollow = null;
  }

  /** A dimension's label: the measured length (the live width) at the middle of its (offset) line. */
  private updateDimensionLabel(type: RegisteredSketchObjectType | null, frame: SketchObjectFrame): void {
    if (!type?.dimension) {
      this.removeDimensionLabel();
      return;
    }
    if (!this.dimensionLabel) {
      this.dimensionLabel = new DimensionLabel(this.viewer, this);
      this.dimensionLabel.setSelected(this.selected);
    }
    const id = this._identifier;
    const style = {
      decimals: getSketchObjectDimensionDecimalsProperty(id).value ?? 2,
      showUnit: getSketchObjectDimensionShowUnitProperty(id).value ?? true,
      alongLine: getSketchObjectDimensionTextAlongLineProperty(id).value ?? true,
      size: getSketchObjectDimensionTextSizeProperty(id).value ?? 1,
    };
    const offset = placedDimensionOffset(type, id);
    this.dimensionLabel.set(frame.width, style, { x: frame.norm.x * offset, y: frame.norm.y * offset, z: frame.z }, frame.dir);
  }

  private removeDimensionLabel(): void {
    this.dimensionLabel?.dispose();
    this.dimensionLabel = null;
  }

  /**
   * Show the type's 3D model (organisation objects with one) in 3D views:
   * placed like the Object Editor's preview — stretched with the footprint
   * and height per the definition's scale modes, standing on the floor.
   */
  private updateModel(type: RegisteredSketchObjectType | null, frame: SketchObjectFrame): void {
    const model = type?.custom?.spec.model3d;
    const url = type?.custom?.modelUrl;
    if (!type?.custom || !model || !url) {
      this.removeModel();
      return;
    }
    if (!this.modelGroup) {
      this.modelGroup = new THREE.Group();
      this.modelGroup.name = 'sketch_object_model';
      this.modelGroup.matrixAutoUpdate = false;
      this.add(this.modelGroup);
      this.viewer.scene.objects3DOnly.add(this.modelGroup);
      ensureModelLights(this.viewer.floorplanTool.scene);
    }
    if (this.modelAssetId !== model.assetId) {
      this.modelAssetId = model.assetId;
      this.modelGroup.clear();
      const assetId = model.assetId;
      cloneCustomModel(assetId, url)
        .then((object) => {
          if (this.modelGroup && this.modelAssetId === assetId) this.modelGroup.add(object);
        })
        .catch((error: unknown) => console.warn('sketch object model could not be loaded', error));
    }
    const height = type.getParamValues(this._identifier)[MODEL_HEIGHT_PARAM];
    const world = modelWorldMatrix(model, type.custom.spec.defaultSize, frame, typeof height === 'number' && height > 0 ? height : type.custom.spec.defaultSize.height, frame.z - GLYPH_Z_LIFT);
    // The group lives under this object, which sits at the frame center.
    this.modelGroup.matrix.copy(new THREE.Matrix4().makeTranslation(-frame.center.x, -frame.center.y, 0).multiply(world));
    this.modelGroup.matrixWorldNeedsUpdate = true;
  }

  private removeModel(): void {
    if (!this.modelGroup) return;
    this.viewer.scene.objects3DOnly.delete(this.modelGroup);
    this.remove(this.modelGroup);
    this.modelGroup = null;
    this.modelAssetId = '';
  }

  /** Swap in fresh line geometry (degenerate segment when there's nothing to draw). */
  private setLocalSegments(local: number[]): void {
    this.lineGeometry.dispose();
    this.lineGeometry = new LineSegmentsGeometry();
    this.lineGeometry.setPositions(local);
    this.line.geometry = this.lineGeometry;
    this.line.computeLineDistances();
  }

  /** Glyph segments in world coords, flat [x,y,z, x,y,z, ...]. */
  getWorldGlyphSegments(): readonly number[] {
    return this._worldSegments;
  }

  /** World bounding box of the footprint (camera focus). Null when unresolved. */
  getBoundingBox(): THREE.Box3 | null {
    const frame = this.resolveFrame();
    if (!frame) return null;
    const box = new THREE.Box3();
    for (const corner of frameCorners(frame)) {
      box.expandByPoint(new THREE.Vector3(corner.x, corner.y, frame.z));
    }
    return box.isEmpty() ? null : box;
  }

  select(): void {
    if (this.selected) return;
    this.selected = true;
    this.line.renderOrder = GLYPH_RENDER_ORDER_SELECTED;
    this.applyMaterialColor();
    this.dimensionLabel?.setSelected(true);
    // Selection may add extra segments (type.selectionGlyph — e.g. the beam's
    // centerline), so the line geometry is rebuilt on both transitions.
    this.rebuildGlyph();
  }

  deselect(): void {
    if (!this.selected) return;
    this.selected = false;
    this.line.renderOrder = GLYPH_RENDER_ORDER;
    this.applyMaterialColor();
    this.dimensionLabel?.setSelected(false);
    this.rebuildGlyph();
  }

  private unsubscribeAll(): void {
    this._colorSubscription?.unsubscribe();
    this._visibleSubscription?.unsubscribe();
    this._geometrySubscriptions.forEach((s) => s.unsubscribe());
    this._geometrySubscriptions = [];
    for (const subs of this._wallSubscriptions.values()) subs.forEach((s) => s.unsubscribe());
    this._wallSubscriptions.clear();
    this.stopWallFollow();
  }

  dispose(): void {
    this.removeModel();
    this.removeDimensionLabel();
    this.viewer.removeEventListener('length_unit_changed', this.onLengthUnitChanged);
    this.unsubscribeTypes();
    this.unsubscribeAll();
    this.lineGeometry.dispose();
    this.lineMaterial.dispose();
  }
}

/**
 * The plan scene has no lights (its own content is unlit), so the lit
 * materials of custom objects' models get a soft hemisphere + key light,
 * added once when the first model appears.
 */
function ensureModelLights(scene: THREE.Scene): void {
  if (scene.getObjectByName('sketch_object_model_lights')) return;
  const lights = new THREE.Group();
  lights.name = 'sketch_object_model_lights';
  lights.add(new THREE.HemisphereLight(0xffffff, 0x8a8f96, 1.4));
  const key = new THREE.DirectionalLight(0xffffff, 1.2);
  key.position.set(1, -1.5, 3);
  lights.add(key);
  scene.add(lights);
}

/** Dashed footprint with a cross: an object whose type is not available. */
function placeholderGlyph(frame: SketchObjectFrame): number[] {
  const out: number[] = [];
  const hw = frame.width / 2;
  const hd = frame.depth / 2;
  pushDashedRect(out, frame, -hw, hw, -hd, hd);
  pushDashedSegment(out, at(frame, -hw, -hd), at(frame, hw, hd), frame.z);
  pushDashedSegment(out, at(frame, -hw, hd), at(frame, hw, -hd), frame.z);
  return out;
}

/**
 * Pick the sketch object whose glyph is nearest a pixel-space cursor, within
 * `radiusPx`. Pixel-space segment distance for the same reasons the wall
 * sketch's pickers document (raycaster precision at UTM scale + deep zoom).
 */
export function pickSketchObjectAtPixel(viewer: Viewer, mousePx: THREE.Vector2, hitTester: PixelHitTester, radiusPx = 8): { object: PointoramaSketchObject; distancePx: number } | null {
  let best: PointoramaSketchObject | null = null;
  let bestDist = radiusPx;
  for (const obj of viewer.scene.sketchObjects) {
    // `.visible` catches user-hidden objects; the pick-block check catches
    // blacklist hides (per-floor filtering), which only flip `.visible`
    // frame-locally during render.
    if (!obj.visible || isAnnotationPickBlocked(viewer, obj, 'SKETCH_OBJECT')) continue;
    const segs = obj.getWorldGlyphSegments();
    for (let i = 0; i + 5 < segs.length; i += 6) {
      const a = hitTester.projectWorldToPixel({ x: segs[i], y: segs[i + 1], z: segs[i + 2] });
      const b = hitTester.projectWorldToPixel({ x: segs[i + 3], y: segs[i + 4], z: segs[i + 5] });
      if (!a || !b) continue;
      const d = PixelHitTester.pointToSegmentDistancePx(mousePx, a, b);
      if (d < bestDist) {
        bestDist = d;
        best = obj;
      }
    }
  }
  return best ? { object: best, distancePx: bestDist } : null;
}
